import requestAccount, { RequestAccountParameter } from "@cogentCustomBlueprint/Handlers/RequestAccount";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    requestAccount,
    signIn,
    validatePassword,    
    loadCurrentCountries,
    getCurrentCountries,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    countries: getCurrentCountries(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    
    fieldGridItem?: GridItemProps;
    fieldTextField?: TextFieldPresentationProps;
    fieldTextAreaField?: TextAreaPresentationProps;
    stateSelectField?: SelectPresentationProps;

    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
}

interface FormValues {
    companyName: string;
    companyAddress: string;
    city: string;
    state: string;
    zip: string;
    contactName: string;
    title: string;
    businessType: string;
    phoneNo: string;
    ext: string;
    email: string;
    message: string;
}

interface FormErrors {
    companyName: string;
    companyAddress: string;
    city: string;
    state: string;
    zip: string;
    contactName: string;
    title: string;
    businessType: string;
    phoneNo: string;
    ext: string;
    email: string;
    message: string;
}

export const requestAccountAccountStyles: RequestAccountStyles = {
    form: {
        css: css`
        label > span {
            color: red;
        }
        `
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    fieldGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    }
};

const styles = requestAccountAccountStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");

const RequestAccount = ({
    requestAccount,
    loadCurrentCountries,
    validateReCaptcha,
    countries,
    returnUrl,
}: Props) => {
    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    });


    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const validationRules: Record<keyof FormValues, {
        required: boolean,
        validator?: (value: string) => boolean,
        message: string
    }> = {
        companyName: {
            required: true,
            message: 'Company name is required',
        },
        companyAddress: {
            required: true,
            message: 'Company address is required',
        },
        city: {
            required: true,
            message: 'City is required',
        },
        state: {
            required: true,
            message: 'State is required',
        },
        zip: {
            required: true,
            message: 'Zip is required',
        },
        contactName: {
            required: true,
            message: 'Contact Name is required',
        },
        title: {
            required: true,
            message: 'Title is required',
        },
        businessType: {
            required: true,
            message: 'Business Type is required',
        },
        phoneNo: {
            required: true,
            message: 'Phone number is required',
        },
        ext: {
            required: false,
            message: '',
        },
        email: {
            required: true,
            validator: (value) => emailRegexp.test(value),
            message: 'Please enter a valid email',
        },
        message: {
            required: false,
            message: '',
        },
    };

    const validateField = (field: keyof FormValues, value: string): string => {
        const rule = validationRules[field];
        if (rule.required && !value.trim()) {
            return rule.message;
        }
        if (rule.validator && !rule.validator(value)) {
            return rule.message;
        }
        return '';
    };

    const initialFormValues: FormValues = {
        companyName: '',
        companyAddress: '',
        city: '',
        state: '',
        zip: '',
        contactName: '',
        title: '',
        businessType: '',
        phoneNo: '',
        ext: '',
        email: '',
        message: '',
    };
    
    const initialFormErrors: FormErrors = {
        companyName: '',
        companyAddress: '',
        city: '',
        state: '',
        zip: '',
        contactName: '',
        title: '',
        businessType: '',
        phoneNo: '',
        ext: '',
        email: '',
        message: '',
    };

    const [formValues, setFormValues] = useState<FormValues>(initialFormValues);
    const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);

    const handleInputChange = (field: keyof FormValues, value: string) => {
        setFormValues(prev => ({ ...prev, [field]: value }));
        if (isSubmitted) { 
            const errorMessage = validateField(field, value);
            setFormErrors(prev => ({ ...prev, [field]: errorMessage }));
            validateForm();
        }
    };

    const validateForm = (): boolean => {
        const errors: FormErrors = {} as FormErrors;
        let isValid = true;
        Object.keys(formValues).forEach((key) => {
            const field = key as keyof FormValues;
            const value = formValues[field];
            const errorMessage = validateField(field, value);
            errors[field] = errorMessage;
            if (errorMessage) {isValid = false;}
        });
        setFormErrors(errors);
        setIsSubmitEnabled(isValid);
        return isValid;
    };

    const onSuccessHandler = () => {
        // Clear form values
        setFormValues(initialFormValues);
        // Clear form errors
        setFormErrors(initialFormErrors);
        // Reset submission flags
        setIsSubmitted(false);
        setIsSubmitting(false);
        setIsSubmitEnabled(false);
        toasterContext.addToast({ body: translate("Successfully Requested for an Account!"), messageType: "success" });
    };

    const onErrorHandler = (error: string) => {
        setIsSubmitting(false); // Reset submitting state on error
        setErrorMessage(error);
        toasterContext.addToast({ body: translate("Failed to Request for an Account!"), messageType: "danger" });
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitted(true);

        const isValid = validateForm();
        if (!isValid) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(true);

        // Prepare the request data
        const requestAccountData: RequestAccountParameter = {
            companyName: formValues.companyName,
            companyStreetAddress: formValues.companyAddress,
            contactName: formValues.contactName,
            city: formValues.city,
            state: formValues.state,
            zipCode: formValues.zip,
            phoneNumber: formValues.phoneNo,
            phoneNumberExt: formValues.ext,
            emailAddress: formValues.email,
            positionTitle: formValues.title,
            typeOfBusiness: formValues.businessType,
            message: formValues.message,
        };

        // Call the requestAccount function with the prepared data
        requestAccount(
            {
                ...requestAccountData,
                onSuccess: onSuccessHandler,
                onError: onErrorHandler,
            }
        );
        
    };

   
    const countryObj = countries != null ? countries.find(x => x.abbreviation === 'US') : null;

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem {...styles.userGridItem}>
                    <GridContainer {...styles.userGridContainer}>
                        
                        <GridItem {...styles.fieldGridItem}> 
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Company Name")}
                                onChange={(e) => handleInputChange('companyName', e.target.value)}
                                value={formValues.companyName}
                                data-test-selector="requestAccount_ComapnyName"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.companyName}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Company Address")}
                                onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                                value={formValues.companyAddress}
                                data-test-selector="requestAccount_CompanyAddress"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.companyAddress}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("City")}
                                onChange={(e) => handleInputChange('city', e.target.value)}
                                value={formValues.city}
                                data-test-selector="requestAccount_City"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.city}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            {countries !== undefined && (
                                <Select
                                    label={translate("Select State")}
                                    required
                                    placeholder="Select"
                                    onChange={(e) => handleInputChange('state', e.target.value)}
                                    value={formValues.state}
                                    error={isSubmitted && formErrors.state}
                                >
                                    <option value="">{translate("Select State")}</option>
                                    {countryObj === undefined
                                        ? countries!
                                                .find(c => c.abbreviation === countries[0].abbreviation)!
                                                .states!.map(st => (
                                                    <option key={st.id} value={st.name}>
                                                        {st.name}
                                                    </option>
                                                ))
                                        : countries!
                                                .find(c => c.abbreviation === countryObj?.abbreviation)!
                                                .states!.map(st => (
                                                    <option key={st.id} value={st.name}>
                                                        {st.name}
                                                    </option>
                                                ))}
                                </Select>
                            )}


                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Zip")}
                                onChange={(e) => handleInputChange('zip', e.target.value)}
                                value={formValues.zip}
                                data-test-selector="requestAccount_Zip"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.zip}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Contact Name")}
                                onChange={(e) => handleInputChange('contactName', e.target.value)}
                                value={formValues.contactName}
                                data-test-selector="requestAccount_ContactName"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.contactName}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Title")}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                value={formValues.title}
                                data-test-selector="requestAccount_Title"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.title}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Business Type")}
                                onChange={(e) => handleInputChange('businessType', e.target.value)}
                                value={formValues.businessType}
                                data-test-selector="requestAccount_BusinessType"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.businessType}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Phone No")}
                                onChange={(e) => handleInputChange('phoneNo', e.target.value)}
                                value={formValues.phoneNo}
                                data-test-selector="requestAccount_PhoneNo"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.phoneNo}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                {...styles.fieldTextField}
                                type="text"
                                label={translate("Ext.")}
                                onChange={(e) => handleInputChange('ext', e.target.value)}
                                value={formValues.ext}
                                data-test-selector="requestAccount_Ext"
                                maxLength={256}
                                // error={isSubmitted}
                            />
                        </GridItem>
                        
                        <GridItem {...styles.emailGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="email"
                                label={translate("Email Address")}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                value={formValues.email}
                                data-test-selector="requestAccount_EmailAddress"
                                maxLength={256}
                                required
                                error={isSubmitted && formErrors.email}
                            />
                        </GridItem>

                        <GridItem {...styles.fieldGridItem}>
                            <TextArea
                                {...styles.fieldTextAreaField}
                                label={translate("Message")}
                                value={formValues.message}
                                onChange={(e) => handleInputChange('message', e.target.value)}
                                data-test-selector="requestAccount_Message"
                            />
                        </GridItem>
                        
                        
                        {errorMessage && (
                            <GridItem data-test-selector="requestAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                            </GridItem>
                        )}
                        <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                        </GridItem>
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting || !isSubmitEnabled}
                                data-test-selector="requestAccount_createButton"
                            >
                                Send
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestAccount),
    definition: {
        group: "My Account",
    },
};

export default widgetModule;
